/**
 * Whether the signed-in person is in a test workspace: one that Avrio staff
 * set up for a prospect to try the product. Real job seekers matched to the
 * prospect's jobs appear there, but can never be contacted, invited or
 * unlocked; the testers can invite only their own test candidates.
 *
 * GET /api/users/me/workspace answers {testWorkspace, jobSeekersAnonymised}
 * for any signed-in recruiter or hiring manager. A backend from before that
 * endpoint answers 404. Every answer that is not both fields as booleans --
 * a 404, any other error, a network failure, a missing or malformed field --
 * reads as an ordinary workspace, so nothing is shown. A test workspace that
 * goes unmarked costs a sentence of context; an ordinary customer told they
 * are in a test would be told something false.
 *
 * ONCE PER SESSION. The shell (components/layout) is rendered by each page,
 * not once above the router, so it mounts again on every navigation. The
 * answer is therefore kept here, for as long as the same session token is in
 * storage: one request per sign-in, and every page after the first draws the
 * banner on its first frame instead of popping it in. Signing out and in again
 * puts a new token in storage, which asks again.
 */

import { useEffect, useState } from 'react';
import axios from '../../../axiosConfig';
import { TOKEN_KEY } from '../../../utils/session';

export interface Workspace {
  testWorkspace: boolean;
  jobSeekersAnonymised: boolean;
}

export const WORKSPACE_PATH = '/api/users/me/workspace';

/** What every failure reads as: an ordinary workspace, which shows nothing. */
export const ORDINARY_WORKSPACE: Workspace = Object.freeze({
  testWorkspace: false,
  jobSeekersAnonymised: false,
});

const readWorkspace = (data: unknown): Workspace => {
  const body = (data ?? {}) as Record<string, unknown>;
  const { testWorkspace, jobSeekersAnonymised } = body;
  if (
    typeof testWorkspace !== 'boolean' ||
    typeof jobSeekersAnonymised !== 'boolean'
  ) {
    return ORDINARY_WORKSPACE;
  }
  return testWorkspace
    ? { testWorkspace, jobSeekersAnonymised }
    : ORDINARY_WORKSPACE;
};

const fetchWorkspace = async (): Promise<Workspace> => {
  try {
    const { data } = await axios.get<unknown>(WORKSPACE_PATH);
    return readWorkspace(data);
  } catch {
    return ORDINARY_WORKSPACE;
  }
};

interface SessionAnswer {
  token: string;
  request: Promise<Workspace>;
  /** Set once the request settles. */
  answer?: Workspace;
}

let session: SessionAnswer | undefined;

const sessionToken = (): string | null => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    // Storage that cannot be read (Safari private mode, a locked-down
    // webview) is treated as no session, as utils/session does.
    return null;
  }
};

/** This session's answer, asked for at most once per session token. */
export const loadWorkspace = (): Promise<Workspace> => {
  const token = sessionToken();
  // Never asked without a session: the 401 that would come back sends the
  // whole app to the sign-in page.
  if (!token) return Promise.resolve(ORDINARY_WORKSPACE);
  if (session?.token === token) return session.request;

  const entry: SessionAnswer = { token, request: fetchWorkspace() };
  entry.request.then((answer) => {
    entry.answer = answer;
  });
  session = entry;
  return entry.request;
};

/** This session's answer if it has already arrived, else undefined. */
export const settledWorkspace = (): Workspace | undefined => {
  const token = sessionToken();
  return token && session?.token === token ? session.answer : undefined;
};

/**
 * The workspace for the signed-in person. Ordinary until the answer arrives,
 * so nothing is drawn, or held open, while it loads.
 */
export const useWorkspace = (): Workspace => {
  const [workspace, setWorkspace] = useState<Workspace>(
    () => settledWorkspace() ?? ORDINARY_WORKSPACE,
  );

  useEffect(() => {
    let cancelled = false;
    loadWorkspace().then((next) => {
      if (!cancelled) setWorkspace(next);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return workspace;
};
